import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { Check, ChevronsUpDown, X } from 'lucide-react';
import { z } from 'zod';

import { PolicyReviewBulkCreateRequest } from '@/types/service/policyUserReview';

import { cn } from '@/lib/utils';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { SimpleDatePicker } from '@/components/shared/SimpleDatePicker';

import { useGetPoliciesQuery } from '@/store/api/policyApi';
import { useGetPolicyGroupsQuery } from '@/store/api/policyGroupApi';

import { zodResolver } from '@hookform/resolvers/zod';

const bulkReviewSchema = z.object({
  policy_id: z.number({ required_error: 'Policy is required' }).min(1, 'Policy is required'),
  group_id: z.number({ required_error: 'Group is required' }).min(1, 'Group is required'),
  review_period_start: z.date({ required_error: 'Start date is required' }),
  review_period_end: z.date({ required_error: 'End date is required' }),
});

type BulkReviewFormData = z.infer<typeof bulkReviewSchema>;

interface CreateBulkReviewFormProps {
  onSubmit: (data: PolicyReviewBulkCreateRequest) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
}

const CreateBulkReviewForm = ({ onSubmit, onCancel, isSubmitting }: CreateBulkReviewFormProps) => {
  const { data: policiesData, isLoading: policiesLoading } = useGetPoliciesQuery();
  const { data: groupsData, isLoading: groupsLoading } = useGetPolicyGroupsQuery();

  const [policyOpen, setPolicyOpen] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);

  const {
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<BulkReviewFormData>({
    resolver: zodResolver(bulkReviewSchema),
    defaultValues: {
      policy_id: 0,
      group_id: 0,
      review_period_start: new Date(),
      review_period_end: (() => {
        const date = new Date();
        date.setDate(date.getDate() + 30);
        return date;
      })(),
    },
  });

  const policyId = watch('policy_id');
  const groupId = watch('group_id');
  const reviewPeriodStart = watch('review_period_start');
  const reviewPeriodEnd = watch('review_period_end');

  const handleFormSubmit = async (data: BulkReviewFormData) => {
    try {
      // Convert dates to ISO date strings for API
      const payload: PolicyReviewBulkCreateRequest = {
        policy_id: data.policy_id,
        group_id: data.group_id,
        review_period_start: data.review_period_start.toISOString().split('T')[0],
        review_period_end: data.review_period_end.toISOString().split('T')[0],
      };
      await onSubmit(payload);
      reset();
    } catch (error) {
      // Error handled by parent
    }
  };

  const policies = policiesData?.policies || [];
  const groups = groupsData?.groups || [];

  const selectedPolicy = policies.find((p) => p.id === policyId);
  const selectedGroup = groups.find((g) => g.id === groupId);

  return (
    <div className='border rounded-lg p-6 bg-blue-50 space-y-4'>
      <div className='flex items-center justify-between'>
        <div>
          <h3 className='text-lg font-semibold'>Create Bulk Review</h3>
          <p className='text-sm text-muted-foreground'>
            Create policy reviews for all members of a group
          </p>
        </div>
        <Button variant='ghost' size='icon' onClick={onCancel} disabled={isSubmitting}>
          <X className='h-5 w-5' />
        </Button>
      </div>

      <form onSubmit={handleSubmit(handleFormSubmit)} className='space-y-4'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          {/* Policy Selection */}
          <div className='space-y-2'>
            <Label>
              Policy <span className='text-destructive'>*</span>
            </Label>
            <Popover open={policyOpen} onOpenChange={setPolicyOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant='outline'
                  role='combobox'
                  className={cn('w-full justify-between', !policyId && 'text-muted-foreground')}
                  disabled={policiesLoading || isSubmitting}
                >
                  {selectedPolicy ? selectedPolicy.name : 'Select policy...'}
                  <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-[400px] p-0'>
                <Command>
                  <CommandInput placeholder='Search policies...' />
                  <CommandList>
                    <CommandEmpty>No policy found.</CommandEmpty>
                    <CommandGroup>
                      {policies.map((policy) => (
                        <CommandItem
                          key={policy.id}
                          value={policy.name}
                          onSelect={() => {
                            setValue('policy_id', policy.id);
                            setPolicyOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              policy.id === policyId ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                          {policy.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {errors.policy_id && (
              <p className='text-sm text-destructive'>{errors.policy_id.message}</p>
            )}
          </div>

          {/* Group Selection */}
          <div className='space-y-2'>
            <Label>
              Group <span className='text-destructive'>*</span>
            </Label>
            <Popover open={groupOpen} onOpenChange={setGroupOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant='outline'
                  role='combobox'
                  className={cn('w-full justify-between', !groupId && 'text-muted-foreground')}
                  disabled={groupsLoading || isSubmitting}
                >
                  {selectedGroup ? selectedGroup.name : 'Select group...'}
                  <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-[400px] p-0'>
                <Command>
                  <CommandInput placeholder='Search groups...' />
                  <CommandList>
                    <CommandEmpty>No group found.</CommandEmpty>
                    <CommandGroup>
                      {groups.map((group) => (
                        <CommandItem
                          key={group.id}
                          value={group.name}
                          onSelect={() => {
                            setValue('group_id', group.id);
                            setGroupOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              group.id === groupId ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                          {group.name} ({group.member_count} members)
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {errors.group_id && (
              <p className='text-sm text-destructive'>{errors.group_id.message}</p>
            )}
          </div>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          {/* Review Period Start */}
          <div>
            <SimpleDatePicker
              label='Review Period Start'
              value={reviewPeriodStart}
              onChange={(date) => date && setValue('review_period_start', date)}
              id='review_period_start'
              disabled={isSubmitting}
              required
            />
            {errors.review_period_start && (
              <p className='text-sm text-destructive mt-1'>{errors.review_period_start.message}</p>
            )}
          </div>

          {/* Review Period End */}
          <div>
            <SimpleDatePicker
              label='Review Period End'
              value={reviewPeriodEnd}
              onChange={(date) => date && setValue('review_period_end', date)}
              id='review_period_end'
              disabled={isSubmitting}
              required
            />
            {errors.review_period_end && (
              <p className='text-sm text-destructive mt-1'>{errors.review_period_end.message}</p>
            )}
          </div>
        </div>

        <p className='text-sm text-muted-foreground'>
          All members of the selected group will be assigned this policy review.
        </p>

        <div className='flex gap-2 justify-end'>
          <Button type='button' variant='outline' onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type='submit' disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create Bulk Review'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateBulkReviewForm;
